import React, { useMemo } from "react";
import clsx from "clsx";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";

import WvhjLogo from "@assets/wvhj-2026-badge.png";

import { INavBarModule } from "@app/api/modules/nav-bar/navbar.module";
import { ImageComponent } from "@app/core";
import { useScrollToTop } from "@app/util/hooks/use-scroll-to-top";
import useScrollDirection, { Direction } from "@app/util/hooks/use-scroll-direction";
import { useIsNetherlands } from "@app/util/hooks/use-is-netherlands";
import { NAVIGATION_HEIGHT_DEFAULT } from "@app/constants/overviews-numbers";
import { formatMessage } from "@app/translations/intl";
import { useAppSelector } from "@app/redux/store";

import { MenuItems } from "./menu-items/menu-items.component";
import { NavbarSearch } from "./navbar-search/navbar-search.component";
import { MyDealAlertsLink } from "../my-deal-alerts/my-deal-alerts-link";
import styles from "./new-nav-bar-component.module.scss";

export interface INewNavBarComponentProps {
  navBarModule: INavBarModule;
  className?: string;
}

const NavBar = (props: INewNavBarComponentProps & RouteComponentProps) => {
  const { user } = useAppSelector((state) => state.auth);
  const { scrollToTop } = useScrollToTop();
  const isNetherlands = useIsNetherlands();

  return (
    <div className={clsx(styles.navbar, props.className)}>
      <div className="uk-container">
        <div className={styles.top}>
          <div className={styles.logos}>
            <div className={styles.logo}>
              <Link to={formatMessage({ id: "global.homepageLink" })} onClick={scrollToTop}>
                <ImageComponent
                  src={props.navBarModule.logo.src}
                  alt={props.navBarModule.logo.alt}
                  title={props.navBarModule.logo.title}
                  isBlocking
                />
              </Link>
            </div>
            {isNetherlands && (
              <ImageComponent
                src={WvhjLogo}
                alt="Website van het jaar 2026"
                title="Website van het jaar 2026"
                className={styles.wvhjLogo}
                isBlocking
              />
            )}
          </div>
          <NavbarSearch
            popularMerchants={props.navBarModule.popularMerchants}
            popularDeals={props.navBarModule.popularDeals}
          />
          <div className={styles.right}>
            {user ? (
              <MyDealAlertsLink className={styles.myDealAlertsLink} />
            ) : (
              <Link to={props.navBarModule.navBarLink || ""} className={styles.rightText}>
                <span className={styles.title}>{props.navBarModule.navBarTitle}</span>
                <span className={styles.subtitle}>{props.navBarModule.navBarSubTitle}</span>
              </Link>
            )}
          </div>
        </div>
      </div>
      <div className={styles.bottom}>
        <div className="uk-container">
          <MenuItems items={props.navBarModule.menuItems} />
        </div>
      </div>
    </div>
  );
};

const component = (props: INewNavBarComponentProps & RouteComponentProps) => {
  const { scrollDir, scrollPosition } = useScrollDirection({ thr: 100 });

  const navBar = useMemo(() => <NavBar {...props} />, []);

  return (
    <div
      style={{
        marginBottom: `${NAVIGATION_HEIGHT_DEFAULT}px` // Height of navbar
      }}
    >
      <div
        className={clsx(
          styles.navBarWrapper,
          (scrollDir === Direction.Up || scrollPosition.top <= NAVIGATION_HEIGHT_DEFAULT) && styles.sticky
        )}
      >
        {navBar}
      </div>
    </div>
  );
};

export const DesktopNavBar = withRouter(component);
